/**
 * Calculate similarity between two strings using multiple algorithms
 * @param {string} str1 - First string to compare
 * @param {string} str2 - Second string to compare
 * @returns {number} - Similarity score between 0 and 1
 */
export function calculateSimilarity(str1, str2) {
  if (!str1 || !str2) return 0;
  
  // Normalize strings
  const s1 = normalizeString(str1);
  const s2 = normalizeString(str2);
  
  // If normalized strings are identical
  if (s1 === s2) return 1;
  
  // Calculate different similarity metrics
  const jaroWinklerScore = jaroWinklerSimilarity(s1, s2);
  const cosineScore = cosineSimilarity(s1, s2);
  const levenshteinScore = 1 - (levenshteinDistance(s1, s2) / Math.max(s1.length, s2.length));
  
  // Weighted average of different scores
  return (jaroWinklerScore * 0.4 + cosineScore * 0.3 + levenshteinScore * 0.3);
}

/**
 * Normalize string for comparison
 */
function normalizeString(str) {
  return str
    .toLowerCase()
    .trim()
    .replace(/[^\w\s]/g, '') // Remove punctuation
    .replace(/\s+/g, ' '); // Normalize whitespace
}

/**
 * Calculate Jaro-Winkler similarity
 */
function jaroWinklerSimilarity(s1, s2) {
  const jaroScore = jaroSimilarity(s1, s2);
  const prefixLength = commonPrefixLength(s1, s2, 4);
  return jaroScore + (prefixLength * 0.1 * (1 - jaroScore));
}

/**
 * Calculate Jaro similarity
 */
function jaroSimilarity(s1, s2) {
  if (s1 === s2) return 1;
  
  const matchWindow = Math.floor(Math.max(s1.length, s2.length) / 2) - 1;
  const s1Matches = new Array(s1.length).fill(false);
  const s2Matches = new Array(s2.length).fill(false);
  
  let matches = 0;
  let transpositions = 0;
  
  // Find matches
  for (let i = 0; i < s1.length; i++) {
    const start = Math.max(0, i - matchWindow);
    const end = Math.min(i + matchWindow + 1, s2.length);
    
    for (let j = start; j < end; j++) {
      if (s2Matches[j] || s1[i] !== s2[j]) continue;
      s1Matches[i] = true;
      s2Matches[j] = true;
      matches++;
      break;
    }
  }
  
  if (matches === 0) return 0;
  
  // Count transpositions
  let k = 0;
  for (let i = 0; i < s1.length; i++) {
    if (!s1Matches[i]) continue;
    while (!s2Matches[k]) k++;
    if (s1[i] !== s2[k]) transpositions++;
    k++;
  }
  
  return (matches / s1.length + matches / s2.length + 
    (matches - transpositions / 2) / matches) / 3;
}

/**
 * Get common prefix length
 */
function commonPrefixLength(s1, s2, maxLength = 4) {
  let i = 0;
  while (i < Math.min(s1.length, s2.length, maxLength) && s1[i] === s2[i]) {
    i++;
  }
  return i;
}

/**
 * Calculate cosine similarity based on word vectors
 */
function cosineSimilarity(s1, s2) {
  const words1 = s1.split(' ');
  const words2 = s2.split(' ');
  
  // Create frequency maps
  const freq1 = {};
  const freq2 = {};
  
  words1.forEach(word => freq1[word] = (freq1[word] || 0) + 1);
  words2.forEach(word => freq2[word] = (freq2[word] || 0) + 1);
  
  // Get all unique words
  const allWords = new Set([...Object.keys(freq1), ...Object.keys(freq2)]);
  
  // Calculate dot product and magnitudes
  let dotProduct = 0;
  let magnitude1 = 0;
  let magnitude2 = 0;
  
  allWords.forEach(word => {
    const f1 = freq1[word] || 0;
    const f2 = freq2[word] || 0;
    dotProduct += f1 * f2;
    magnitude1 += f1 * f1;
    magnitude2 += f2 * f2;
  });
  
  if (magnitude1 === 0 || magnitude2 === 0) return 0;
  
  return dotProduct / (Math.sqrt(magnitude1) * Math.sqrt(magnitude2));
}

/**
 * Calculate Levenshtein distance
 */
function levenshteinDistance(s1, s2) {
  const m = s1.length;
  const n = s2.length;
  
  if (m === 0) return n;
  if (n === 0) return m;
  
  const dp = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));
  
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (s1[i - 1] === s2[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        dp[i][j] = Math.min(
          dp[i - 1][j] + 1,     // deletion
          dp[i][j - 1] + 1,     // insertion
          dp[i - 1][j - 1] + 1  // substitution
        );
      }
    }
  }
  
  return dp[m][n];
}

/**
 * Check if two messages are similar enough to be considered duplicates
 * @param {string} message1 - First message
 * @param {string} message2 - Second message
 * @param {number} threshold - Similarity threshold (default: 0.85)
 * @returns {boolean} - True if messages are similar enough
 */
export function areMessagesSimilar(message1, message2, threshold = 0.85) {
  const similarity = calculateSimilarity(message1, message2);
  return similarity >= threshold;
}